import React, { useEffect, useMemo, useRef } from 'react';
import { darken, lighten, Stack, Theme } from '@mui/material';
import useWatchError from '../../../handlingErrors/useWatchError';
import AlertFromError from '../../../handlingErrors/AlertFromError';
import IncidentAPI from '../../../redux/incident/api/IncidentAPI';
import PolicyAPI from '../../../redux/incident/api/PolicyAPI';
import { useDevices } from '../../../redux/commander/hooks/useDevices';
import _ from 'lodash';
import { DataGrid } from '@mui/x-data-grid';
import { useIncidentsDefDataGrid } from './useIncidentsDefDataGrid';
import {
  AlarmState,
  INCIDENTS_POLLING_INTERVAL,
  IncidentAPIResponseMVP,
} from '../../../redux/incident/api/Incident.model';
import { useNavigate } from 'react-router-dom';
import { getPolyglot } from '../../../i18n';
import { useGroupById } from '../../../redux/groups/hooks/useGroupById';

const getRowClassName = (params: any) => {
  const status = !params.row.resolutionTimestamp ? 'unsolved' : 'solved';
  return `custom-theme-class--${status}`;
};

const getRowId = (row: any) => row.uuid;

const getBackgroundColor = (
  color: string,
  theme: Theme,
  coefficient: number
) => ({
  color,
  // use !important to override the default styles on avoid onhover fast bug, because alpha
  backgroundColor: darken(color, coefficient) + '!important',
  ...theme.applyStyles('light', {
    color,
    backgroundColor: lighten(color, coefficient) + '!important',
  }),
});

export interface IncidentListProps {
  workspaceId: string;
  // Encoded group id
  groupId?: string;
  // Encoded device id
  deviceId?: string;
  cuId?: string;
  filterByActive?: boolean;
}
function IncidentList(props: IncidentListProps) {
  const { workspaceId, filterByActive, groupId, deviceId } = props;

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 15,
  });

  const firstIncident = useRef<IncidentAPIResponseMVP | null>(null);

  const {
    data: { data: incidentsData = [], count } = { data: [], count: 0 },
    error: errorIncidents,
    isLoading: isLoadingIncidents,
    isFetching: isFetchingIncidents,
  } = IncidentAPI.useQueryMyIncidentByWorkspaceIdQuery(
    {
      workspaceId,
      params: {
        state: filterByActive ? AlarmState.NEW : undefined,
        page_size: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
        device: deviceId,
        group: !deviceId && groupId ? groupId : undefined,
        after_id:
          paginationModel.page === 0 ? undefined : firstIncident.current?.uuid,
      },
    },
    {
      skip: !workspaceId,
      pollingInterval: INCIDENTS_POLLING_INTERVAL,
    }
  );

  // Use the first incident to not move the cursor when list is updated
  useEffect(() => {
    if (
      incidentsData.length > 0 &&
      !isFetchingIncidents &&
      !isLoadingIncidents &&
      paginationModel.page === 0
    ) {
      firstIncident.current = incidentsData[0];
    }
  }, [paginationModel, incidentsData, isFetchingIncidents, isLoadingIncidents]);

  const {
    error: errorPolicies,
    isLoading: isLoadingPolicies,
    isFetching: isFetchingPolicies,
    data: policiesData,
  } = PolicyAPI.useGetPoliciesQuery(workspaceId, {
    refetchOnMountOrArgChange: true,
    skip: !workspaceId,
  });

  const navigate = useNavigate();
  const polyglot = getPolyglot();

  const ids = incidentsData?.map((item) => item.deviceId);
  const deviceIds = _.uniq(ids);
  const { data: devices, isLoading: isLoadingDevices } = useDevices(deviceIds);

  const isFetching = isFetchingIncidents || isFetchingPolicies;
  const isLoading = isLoadingIncidents || isLoadingPolicies || isLoadingDevices;
  const error = errorIncidents || errorPolicies;
  const { data: group } = useGroupById(groupId ?? '');

  useWatchError(error);

  const incidentsDefDataGrid = useIncidentsDefDataGrid({
    workspaceId,
    devices,
    policiesData,
    group,
    incidentsData,
  });

  return (
    <div>
      <Stack spacing={1} mt={1}>
        <AlertFromError error={error} />
        <DataGrid
          {...incidentsDefDataGrid}
          rows={incidentsData}
          loading={isFetching || isLoading}
          pageSizeOptions={[5, 10, 15, 25, 50]}
          getRowClassName={getRowClassName}
          onRowClick={(params, event, details) => {
            const item = params.row;
            const url = `../alarms/${item.uuid}`;
            navigate(url);
          }}
          getRowId={getRowId}
          autoHeight
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          rowCount={count || 0}
          sx={(theme) => ({
            // pointer cursor on ALL rows
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
            },
            '.custom-theme-class--unsolved': {
              ...getBackgroundColor(theme.palette.error.main, theme, 0.7),
              '&:hover': {
                ...getBackgroundColor(theme.palette.error.main, theme, 0.6),
              },
              '&.Mui-selected': {
                ...getBackgroundColor(theme.palette.error.main, theme, 0.5),
                '&:hover': {
                  ...getBackgroundColor(theme.palette.error.main, theme, 0.4),
                },
              },
            },
          })}
          localeText={{
            noRowsLabel: polyglot.t('incident.no_alarms'),
          }}
        />
      </Stack>
    </div>
  );
}

export default IncidentList;
